<template>
  <Loading v-if="loading" />
  <Modal v-if="showModalQuickReservation" @close="closeThisModal">
    <div class="quickReservation">
      <div class="quickReservation__header">
        <span class="title">
          {{ isViewOrder ? $t('reserveOrder') : $t('quickReservation') }}
        </span>
        <img
          src="@/assets/icons/CloseModal.svg"
          alt="CloseModal"
          @click="closeThisModal"
        />
      </div>
      <div class="quickReservation__body">
        <Dropdown
          v-if="initLockers()"
          class="quickReservation__body-dropdown"
          :selected-option="selectedPickupBox"
          :options="pickupBoxOptions"
          @select-option="changePickupBox"
        />
        <FormKit
          v-if="activePickupBox?.info.active && !disableContent"
          id="quickReservation-form"
          v-model="formData"
          type="form"
          :actions="false"
          @submit="submitHandler"
        >
          <div v-if="initLockers()">
            <div
              v-for="(locker, index) in lockers"
              :key="index"
              class="quickReservation__body-lockers"
              :data-testid="'quickReservation' + index"
            >
              <div class="lockers-info">
                <div class="size-locker">
                  <p>{{ $t(locker.label) }}</p>
                </div>
                <div class="quantity-lockers">
                  <p>{{ quantityLockers[locker.code] }}</p>
                </div>
              </div>
              <Input
                type="number"
                :name="locker.label"
                :max="1"
                :min="0"
                :disabled="chooseLocker || quantityLockers[locker.code] === 0"
                @click="handleChange"
              />
            </div>
          </div>
          <div v-if="initPharmacy()">
            <p class="warning">{{ $t('deliveryToThePharmacy') }}</p>
          </div>
          <Checkbox
            v-if="pharmacy.strongAuthentication !== 'off'"
            :class="{
              disabledCheckbox: pharmacy.strongAuthentication === 'off'
            }"
            name="is_drug"
            :value="formData.is_drug"
            :title="$t('isDrug')"
            :disabled="pharmacy.strongAuthentication === 'off'"
            @change="formData.is_drug = !formData.is_drug"
          />
        </FormKit>
        <div
          v-if="!activePickupBox?.info.active"
          class="quickReservation__body-inactiveBox"
        >
          <p>{{ $t('pickupboxIsInactive') }}</p>
        </div>
        <div v-if="disableContent" class="quickReservation__body-inactiveBox">
          <p>{{ $t('noSpaceInPickupBoxes') }}</p>
        </div>
      </div>
      <div class="quickReservation__buttons">
        <Button
          class="quickReservation__buttons-button"
          :variant="
            defaultQuantityLockers !== quantityLockers ||
            viewOrder?.deliveryMethod === 'pharmacy'
              ? 'filled'
              : 'disabled'
          "
          :title="$t('reserve')"
          :disabled="
            defaultQuantityLockers !== quantityLockers ||
            viewOrder?.deliveryMethod === 'pharmacy'
              ? false
              : true
          "
          @click="submitForm"
        />
        <Button
          variant="redBordered"
          :title="$t('cancel')"
          @click="closeThisModal"
        />
      </div>
    </div>
  </Modal>
  <ReservedLockers
    v-if="showModalReservedLockers"
    :show-modal-reserved-lockers="showModalReservedLockers"
    :choosed-lockers="choosedLockers"
    :delivery-id="viewOrder.deliveryId"
    @close-all-modal="closeReservedLockers"
  />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import Loading from '@/views/Loading.vue';
import ReservedLockers from './ReservedLockers.vue';

import {
  DataSortLocker,
  Locker,
  LockersType,
  Module,
  PickupBox
} from '@/store/modules/pharmacy/types';
import { InputFileEvent, Option } from '@/types';

import { lockers, kolliID } from './configs';

@Options({
  name: 'QuickReservation',
  props: ['showModalQuickReservation', 'activeReservations', 'viewOrderData'],
  emits: ['closeModal'],
  components: {
    Loading,
    ReservedLockers
  },
  data() {
    return {
      formData: {
        lockerId: [],
        deliveryId: undefined,
        is_drug: false
      },
      selectedPickupBox: {
        value: '',
        label: ''
      },
      payload: null,
      activePickupBox: null,
      lockersId: [],
      choosedLockers: [],
      validationRules: {},
      pickupBoxOptions: [],
      numberSelectedLockers: {},
      lockers,
      kolliID,
      selectedLockers: 0,
      quantityLockers: { 0: 0, 1: 0, 2: 0, fridge: 0 },
      defaultQuantityLockers: { 0: 0, 1: 0, 2: 0, fridge: 0 },
      loading: false,
      disableContent: false,
      chooseLocker: false,
      disableButton: false,
      showModalReservedLockers: false
    };
  },
  computed: {
    ...mapState('pharmacy', ['pickupBoxes', 'pharmacy']),
    ...mapState('orders', ['viewOrder']),
    isViewOrder() {
      return this.$route.name === 'ViewOrder';
    },
    isOrdersHistory() {
      return this.$route.name === 'OrdersHistory';
    },
    isAPIReservations() {
      return this.$route.name === 'APIReservations';
    }
  },
  created() {
    this.loadPickupBoxes();
  },
  methods: {
    ...mapActions('pharmacy', [
      'assignDelivery',
      'getPharmacy',
      'getPickupBoxesByPharmacy',
      'createDeliveryFromOrder'
    ]),
    ...mapActions('orders', ['getViewOrderById']),
    async loadPickupBoxes() {
      this.loading = true;
      const { pharmacyId } = this.$route.params;

      if (this.isViewOrder) {
        await this.getPickupBoxesByPharmacy(pharmacyId);
      } else {
        await this.getPickupBoxesByPharmacy(this.pharmacy.id);
      }

      if (this.pickupBoxes) {
        this.initPickupBoxOptions();
      }

      this.loading = false;
    },
    initPickupBoxOptions() {
      this.pickupBoxOptions = this.pickupBoxes.map((pickupBox: PickupBox) => ({
        value: pickupBox.info.id,
        label: pickupBox.info.name,
        img: require('@/assets/icons/PickupBox.svg')
      }));

      this.selectedPickupBox = this.pickupBoxOptions[0];

      this.activePickupBox = this.pickupBoxes[0];
      this.modules = this.activePickupBox?.info.modules;

      this.getQuantityLockers(this.modules);
    },
    changePickupBox(option: Option) {
      this.selectedPickupBox = option;
      this.activePickupBox = this.pickupBoxes.find(
        (pickupBox: PickupBox) => pickupBox.info.id === option.value
      );
      this.modules = this.activePickupBox?.info.modules;

      this.getQuantityLockers(this.modules);
    },
    initLockers() {
      if (this.isAPIReservations) {
        return true;
      }

      if (
        (this.isViewOrder || this.isOrdersHistory) &&
        this.viewOrderData?.deliveryMethod === 'pickupbox'
      ) {
        return true;
      }
    },
    initPharmacy() {
      if (this.isAPIReservations) {
        return false;
      }

      if (
        (this.isViewOrder || this.isOrdersHistory) &&
        this.viewOrderData?.deliveryMethod === 'pharmacy'
      ) {
        return true;
      }
    },
    getVariantButton() {
      if (this.defaultQuantityLockers !== this.quantityLockers) {
        return 'filled';
      } else {
        return 'disabled';
      }
    },
    getQuantityLockers(modules: Module[]) {
      const allModulesLockers = modules
        ?.map((module: Module) => module.lockers)
        .flat();

      const lockersActiveBox = allModulesLockers?.reduce(
        (lockers: LockersType, currentLocker: Locker) => {
          return {
            0:
              lockers[0] +
              +(
                currentLocker.type === 0 &&
                !currentLocker.order &&
                currentLocker.status !== 'inactive'
              ),
            1:
              lockers[1] +
              +(
                currentLocker.type === 1 &&
                !currentLocker.order &&
                !currentLocker.fridge &&
                currentLocker.status !== 'inactive'
              ),
            2:
              lockers[2] +
              +(
                currentLocker.type === 2 &&
                !currentLocker.order &&
                currentLocker.status !== 'inactive'
              ),
            fridge:
              lockers.fridge +
              +(
                currentLocker.fridge &&
                !currentLocker.order &&
                currentLocker.status !== 'inactive'
              )
          };
        },
        { 0: 0, 1: 0, 2: 0, fridge: 0 }
      );

      this.defaultQuantityLockers = lockersActiveBox;
      this.quantityLockers = this.defaultQuantityLockers;

      const valuesIsZero = (currentValue: number) => currentValue === 0;

      if (lockersActiveBox) {
        const valuesLockersActiveBox =
          Object.values(lockersActiveBox).every(valuesIsZero);

        if (valuesLockersActiveBox) {
          return (this.disableContent = true);
        } else {
          return (this.disableContent = false);
        }
      }
    },
    handleChange(e: InputFileEvent) {
      const { name, value } = e.target;

      this.inputValue = value;

      if (name && +value === 1) {
        this.chooseLocker = true;

        if (+value < 0) {
          value;
        }

        if (name === 'small' && this.defaultQuantityLockers[0] !== 0) {
          this.quantityLockers = {
            ...this.quantityLockers,
            0: this.defaultQuantityLockers[0] - +value
          };
        } else if (name === 'medium' && this.defaultQuantityLockers[1] !== 0) {
          this.quantityLockers = {
            ...this.quantityLockers,
            1: this.defaultQuantityLockers[1] - +value
          };
        } else if (name === 'large' && this.defaultQuantityLockers[2] !== 0) {
          this.quantityLockers = {
            ...this.quantityLockers,
            2: this.defaultQuantityLockers[2] - +value
          };
        } else if (
          name === 'fridge' &&
          this.defaultQuantityLockers['fridge'] !== 0
        ) {
          this.quantityLockers = {
            ...this.quantityLockers,
            fridge: this.defaultQuantityLockers['fridge'] - +value
          };
        }
      }
    },
    getNumberSelectedLockers() {
      if (this.defaultQuantityLockers !== this.quantityLockers) {
        this.numberSelectedLockers = {
          0: this.defaultQuantityLockers[0] - this.quantityLockers[0],
          1: this.defaultQuantityLockers[1] - this.quantityLockers[1],
          2: this.defaultQuantityLockers[2] - this.quantityLockers[2],
          fridge:
            this.defaultQuantityLockers['fridge'] -
            this.quantityLockers['fridge']
        };
      }
    },
    getLockersId() {
      this.lockersId = [];

      const allModulesLockers = this.modules
        .map((module: Module) =>
          module.lockers.map((locker: Locker) => ({
            ...locker,
            moduleId: module.position
          }))
        )
        .flat();

      const selectedLockersNumber = Object.entries(
        this.numberSelectedLockers
      ).filter(
        (lockerTypeAmount: [string, unknown]) => lockerTypeAmount[1] !== 0
      );

      selectedLockersNumber.forEach((selectedLocker: [string, unknown]) => {
        const lockersOfOneType = allModulesLockers
          .filter(
            (locker: Locker) =>
              ((locker.type === +selectedLocker[0] &&
                !locker.fridge &&
                !locker.inactive) ||
                (locker.fridge && selectedLocker[0] === 'fridge')) &&
              !locker.order
          )
          .sort(
            (a: DataSortLocker, b: DataSortLocker) => a.openCount - b.openCount
          );

        if (lockersOfOneType.length) {
          const lockerData = selectedLocker[1] as number;

          for (let i = 0; i < lockerData; i++) {
            if (lockersOfOneType[i]) {
              this.lockersId.push(
                `${lockersOfOneType[i].moduleId}-${lockersOfOneType[i].position}`
              );
            }
          }
        }
      });

      return this.lockersId;
    },
    async submitHandler() {
      this.loading = true;
      const { id } = this.$route.params;

      this.getNumberSelectedLockers();
      this.getLockersId();

      if (this.isAPIReservations) {
        const payload = {
          assignToBox: true,
          boxInfo: {
            lockerId: [...this.lockersId],
            boxId: this.activePickupBox.info.id
          }
        };

        await this.assignDelivery({
          data: payload,
          pharmacyId: id,
          deliveryId: this.activeReservations.deliveryId
        });

        await this.getPharmacy(id);

        this.choosedLockers = payload.boxInfo.lockerId;
        this.showModalReservedLockers = true;
      }

      if (this.isViewOrder || this.isOrdersHistory) {
        if (this.viewOrderData?.deliveryMethod === 'pickupbox') {
          this.loading = true;

          this.payload = {
            orderId: this.viewOrderData.id,
            pharmacyId: this.pharmacy.id,
            pickupboxId: this.activePickupBox.id,
            lockerId: [...this.lockersId],
            deliveryId: undefined,
            additionalDeliveryIds: [],
            is_drug: this.formData.is_drug
          };

          await this.createDeliveryFromOrder(this.payload);

          await this.getViewOrderById({
            pharmacyId: this.pharmacy.id,
            orderId: this.viewOrderData.id
          });

          if (this.viewOrder.deliveryId) {
            this.choosedLockers = this.payload.lockerId;
            this.showModalReservedLockers = true;
          }

          this.loading = false;
        } else {
          this.loading = true;
          this.payload = {
            orderId: this.viewOrderData.id,
            pharmacyId: this.pharmacy.id,
            pickupboxId: '',
            lockerId: [],
            deliveryId: undefined,
            additionalDeliveryIds: [],
            is_drug: this.formData.is_drug
          };

          this.closeThisModal();

          await this.createDeliveryFromOrder(this.payload);

          this.loading = false;
        }
      }

      this.loading = false;
    },
    submitForm() {
      this.$formkit.submit('quickReservation-form');
    },
    closeThisModal() {
      this.$emit('closeModal');
      this.$formkit.reset('quickReservation-form');
    },
    closeReservedLockers() {
      this.closeThisModal();
      this.$emit('closeModal');
      this.showModalReservedLockers = false;
    }
  },
  watch: {
    pickupBoxes() {
      if (this.pickupBoxes) {
        this.initPickupBoxOptions();
      }
    }
  }
})
export default class QuickReservation extends Vue {}
</script>

<style lang="scss">
.quickReservation {
  width: 410px;
  height: 100%;
  .quickReservation__header {
    display: flex;
    justify-content: space-between;
    padding: 24px 24px 0 24px;
    .title {
      font-family: 'Ubuntu-Medium';
      font-size: $eighteenSize;
      line-height: 14px;
    }
    img {
      cursor: pointer;
    }
  }
  .quickReservation__body {
    margin: 24px;
    .quickReservation__body-dropdown {
      height: 40px;
      width: 360px;
    }
    .formkit-form {
      margin-top: 24px;
      .quickReservation__body-lockers {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 11px;
        &:last-child {
          margin-bottom: 0;
        }
        .lockers-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 140px;
          .quantity-lockers {
            width: 40px;
            height: 20px;
            text-align: center;
            border: 1px solid $main-green;
            border-radius: 8px;
            p {
              border-radius: 8px;
              margin: 2px 0;
            }
          }
        }
        .input {
          width: 70px;
          height: 40px;
          margin-bottom: 0;
          .formkit-outer {
            height: 40px;
            .formkit-wrapper {
              height: 40px;
              .formkit-inner {
                .formkit-input {
                  height: 40px;
                }
              }
            }
          }
        }
      }
      .warning {
        color: $label;
        font-size: $fourteenSize;
        line-height: $twentyTwoSize;
        font-weight: 500;
      }
      .checkbox {
        margin-top: 32px;
      }
    }
    .quickReservation__body-inactiveBox {
      margin: 30px 0;
      p {
        color: $label;
        font-size: $fourteenSize;
        line-height: $twentyTwoSize;
        font-weight: 500;
      }
    }
  }
  .quickReservation__buttons {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 362px;
    padding: 24px;
    padding-top: 0;
    .button {
      width: 200px;
    }
    .quickReservation__buttons-button {
      margin-right: 12px;
    }
  }
}
</style>
